import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import QCReportSheet from "./QCReportSheet";
import { getQCReportById } from "../../api/qcReports";

// Schema for QC Report Header
export interface QCReportHeader {
    id: string;
    operationDate: string;
    finishedGoodName: string;
    workOrder: string;
    inspectorName: string;
    totalInspected: number;
    totalPassed: number;
    totalFailed: number;
    notes?: string;
    createdAt: string;
    updatedAt: string;
}

// Props interface for the modal component
interface QCReportSheetModalProps {
    props: {
        id: string;
    };
}

export default function QCReportSheetModal({ props }: QCReportSheetModalProps) {
    const { id: reportHeaderId } = props;

    const [reportHeader, setReportHeader] = useState<QCReportHeader | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);


    const fetchReportHeader = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getQCReportById(reportHeaderId);
            setReportHeader(response.data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error fetching report header');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (reportHeaderId) {
            fetchReportHeader();
        }
    }, [reportHeaderId]);

    if (loading) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>Cargando reporte...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error">Error: {error}</Typography>
            </Box>
        );
    }

    if (!reportHeader) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>No se encontró el reporte</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            {/* Header Section */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Reporte de Control de Calidad
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                            ID del Reporte
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {reportHeader.id}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                            Fecha del Reporte
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {reportHeader.operationDate || 'N/A'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                            Producto Terminado
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {reportHeader.finishedGoodName || 'N/A'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                            Orden de Trabajo
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {reportHeader.workOrder || 'N/A'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2" color="textSecondary">
                            Inspector
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {reportHeader.inspectorName}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* QC Report Sheet Component */}
            <QCReportSheet props={{ id: reportHeaderId }} />
        </Box>
    );
} 